import {
  Conversation,
  ConversationLogItem,
} from '@stripe-internal/conversations-client';
import {view, css, useTokenValue, tokens} from '@sail/ui';
import {forwardRef} from 'react';
import {ParticipantJoinedRow} from './ParticipantJoinedRow';
import {ParticipantLeftRow} from './ParticipantLeftRow';
import {ChatEndedRow} from '../../Chat/Row/ChatEndedRow';
import {TranslationRow} from '../../Chat/Row/TranslationRow';
import {MetaEventRow} from './MetaEventRow';
import {GroupedMessagesRow} from './Messages/GroupedMessagesRow';
import {useNewUniversalHelpDesign} from '../../FeatureFlagsProvider';
import {ChatStartedRow} from '../../Chat/Row/ChatStartedRow';
import {CustomComponent} from './Messages/CustomComponent';
import {
  useItemsGroupedV2,
  GroupingOptions,
  isTextConversationMessage,
} from '../../helpers/useItemsGroupedV2';

type Props = {
  conversation: Conversation;
  items: ConversationLogItem[];
  animated?: boolean;
  disableInteraction?: boolean;
};

const groupedMessageTypes = new Set([
  'text-conversation-message',
  'rich-text-conversation-message',
  'attachment-conversation-message',
  'inline-data-conversation-message',
  'articles-conversation-message',
]);

export function renderItemsGroupedV1(
  conversation: Conversation,
  items: ConversationLogItem[],
  {
    useNewDesign,
    useDotComDesign,
    animated,
    disableInteraction,
  }: GroupingOptions,
) {
  const rows = [];

  let translationRowAdded = false; // Flag to track if the TranslationRow has been added
  let conversationParticipantJoined = false; // Flag to track if the agent has joined the chat

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];

    switch (item.name) {
      case 'conversation-participant-joined': {
        conversationParticipantJoined = true;
        rows.push(
          <ParticipantJoinedRow
            key={item.id}
            conversation={conversation}
            item={item}
          />,
        );
        break;
      }
      case 'conversation-participant-left': {
        rows.push(
          <ParticipantLeftRow
            key={item.id}
            conversation={conversation}
            item={item}
          />,
        );
        break;
      }
      case 'conversation-message-added': {
        const participantMessages = [item];
        const {participant} = item.message;

        const isTranslationCase =
          isTextConversationMessage(item.message) &&
          !!item.message.originalText;

        if (
          isTranslationCase &&
          !translationRowAdded &&
          conversationParticipantJoined // Only show this when an agent has joined. Without this condition UH can trigger the row render.
        ) {
          rows.push(<TranslationRow />);
          translationRowAdded = true; // Update the flag to reflect that the TranslationRow has been added
        }

        // walk forward grouping all messages for this participant
        for (let j = i + 1; j < items.length; j += 1) {
          const nextItem = items[j];

          if (nextItem.name === 'conversation-message-added') {
            const isGroupedType =
              nextItem.message.participant.id === participant.id &&
              groupedMessageTypes.has(nextItem.message.type);

            if (isGroupedType) {
              participantMessages.push(nextItem);
              i = j;

              // eslint-disable-next-line no-continue
              continue;
            }
          }

          break;
        }

        rows.push(
          <GroupedMessagesRow
            conversation={conversation}
            key={`grouped-${item.id}`}
            items={participantMessages}
            disableInteraction={disableInteraction}
            animated={animated}
          />,
        );
        break;
      }
      case 'conversation-meta': {
        rows.push(<MetaEventRow key={item.id} item={item} />);
        break;
      }
      case 'conversation-started': {
        if (useNewDesign || useDotComDesign) {
          rows.push(
            <ChatStartedRow key={item.id} conversation={conversation} />,
          );
        }
        break;
      }
      case 'conversation-custom-component': {
        rows.push(<CustomComponent {...item} />);
        break;
      }
      case 'conversation-ended': {
        if (!useNewDesign && !conversation.context.surpressChatEndedNotice) {
          rows.push(<ChatEndedRow key={item.id} />);
        }
        break;
      }
      default:
      // unknown log item
    }
  }

  return rows;
}

export const LogItems = forwardRef<HTMLDivElement, Props>(
  ({conversation, items, animated, disableInteraction}, ref) => {
    const useNewDesign = useNewUniversalHelpDesign();

    let rows = useItemsGroupedV2(conversation, items, {
      useNewDesign,
      useDotComDesign: false,
      animated,
      disableInteraction,
    });

    if (rows == null) {
      rows = renderItemsGroupedV1(conversation, items, {
        useNewDesign,
        useDotComDesign: false,
        animated,
        disableInteraction,
      });
    }

    const largeSpace = parseInt(useTokenValue(tokens.space.large), 10);
    const xsmallSpace = parseInt(useTokenValue(tokens.space.xsmall), 10);
    const tightGap = -largeSpace + xsmallSpace;

    return (
      <view.div
        ref={ref}
        data-testid="conversation-log-items"
        // We add a class name to all the content indicating that new
        // designs are being used so that certain global selectors can
        // target elements without being applied when the old design is
        // being used
        className={useNewDesign ? 'uh-conversations-new-design' : undefined}
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: useNewDesign ? 'large' : 'medium',
        }}
        uses={[
          css.global({
            '.uh-conversations-new-design .uh-conversations-contains-meta-row + .uh-conversations-contains-meta-row':
              {marginTop: '-16px'},

            '.uh-conversations-new-design .uh-conversations-text-message-row[data-message-type="user"] + .uh-conversations-text-message-row[data-message-type="user"]':
              {marginTop: tightGap},
            '.uh-conversations-new-design .uh-conversations-text-message-row[data-message-type="agent"] + .uh-conversations-text-message-row[data-message-type="agent"]':
              {marginTop: tightGap},
            '.uh-conversations-new-design .uh-conversations-text-message-row[data-message-type="agent"] + [data-testid="typing-indicator"]':
              {marginTop: tightGap},

            // Note: all new handling of the border radius tweaks is done in
            // `useItemsGroupedV2`. We still need this for when the
            // `messagesTopAnchored` flag is turned off. This code will go
            // away once we fully roll out the next message scrolling UX
            '.uh-conversations-new-design .uh-conversations-text-message-row[data-message-type="user"]:last-child .uh-conversations-chat-bubble':
              {borderBottomRightRadius: '4px !important'},
            '.uh-conversations-new-design .uh-conversations-text-message-row[data-message-type="agent"]:last-child .uh-conversations-chat-bubble':
              {borderBottomLeftRadius: '4px !important'},

            '.uh-conversations-new-design .uh-conversations-message-added:empty':
              {display: 'none'},
            '.uh-conversations-new-design .uh-conversations-grouped-messages.v2.fill-space:last-of-type':
              {
                minHeight: 'calc(var(--log-height) - 70px)',
              },
          }),
        ]}
      >
        {rows}
      </view.div>
    );
  },
);
